import { IconNames } from '@blueprintjs/icons';
import { Button } from '@strata/react-buttons';
import { MessagingBlock } from '@strata/react-messaging-block';
import { StatusIndicator } from '@strata/react-status-indicators';

import { ContentBox } from 'components/shared/ContentBox';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const grammar = [
    <ul key="1">
        <li>Use proper punctuation</li>
        <li>Use sentence case</li>
    </ul>,
];
const capitalization = [
    <div key="2">
        <h3>Sentence Case</h3>
        <p>
            The first letter of the first word in a sentence is capitalized, and all other letters
            are in lower case unless they are proper nouns or other words that require
            capitalization.
        </p>
        <h4>Examples</h4>
        <ul>
            <li>Body text</li>
            <li>Message block titles</li>
        </ul>
    </div>,
    <div key="3">
        <h3>All Caps</h3>
        <p>Use all caps to provide emphasis on certain important details, but use sparingly.</p>
        <h4>Examples</h4>
        <ul>
            <li>Acronyms</li>
            <li>Buttons</li>
            <li>Data labels</li>
            <li>Dialog Titles</li>
            <li>Statuses</li>
        </ul>
    </div>,
];
const numerals = [
    <p key="4">
        Always use numerals for all numbers in the UI, including 0 and 1 through 9. The only time a
        numeral should be spelled out is in a sentence where it is serving as an article, adjective,
        or pronoun.
    </p>,
    <div key="5">
        <h4>A number as an adjective</h4>
        <p>Select one reason.</p>
    </div>,
    <div key="6">
        <h4>A number as an article</h4>
        <p>Two different options are available.</p>
    </div>,
    <div key="7">
        <h4>A number as a pronoun</h4>
        <p>Out of the options, choose three.</p>
    </div>,
];
const textAlignment = [
    <p key="8">
        Left-aligning text is a standard practice, but there are some scenarios where center-aligned
        text is used.
    </p>,
    <div key="9">
        <h3>Buttons</h3>
        <p>
            Buttons consisting of text within a container should always have the text
            center-aligned.
        </p>
        <ContentBox
            centerContent
            content={
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 16,
                    }}
                >
                    <Button intent={Button.Intent.PRIMARY} label="Primary" />
                    <Button
                        intent={Button.Intent.PRIMARY}
                        label="Dropdown"
                        iconAfterLabel={IconNames.CHEVRON_DOWN}
                    />
                    <Button intent={Button.Intent.NONE} label="Secondary" />
                    <Button intent={Button.Intent.NONE} disabled label="Disabled" />
                    <Button intent={Button.Intent.DANGER} label="Destructive" />
                </div>
            }
        />
    </div>,
    <div key="10">
        <h3>Statuses</h3>
        <p>The status indicator names within the container should always be center-aligned.</p>
        <ContentBox
            centerContent
            content={
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 16,
                    }}
                >
                    <StatusIndicator message="DEFAULT" status="default" />
                    <StatusIndicator message="IN PROGRESS" status="in-progress" />
                    <StatusIndicator message="SUCCESS" status="success" />
                    <StatusIndicator message="WARNING" status="warning" />
                    <StatusIndicator message="CAUTION" status="caution" />
                    <StatusIndicator message="TIP" status="tip" />
                </div>
            }
        />
    </div>,
    <div key="11">
        <h3>Message Blocks</h3>
        <p>
            A message block consists of an icon, title, and message, all of which should be
            center-aligned within the container.
        </p>
        <ContentBox
            centerContent
            content={
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 16,
                    }}
                >
                    <MessagingBlock
                        icon="search"
                        iconStatus=""
                        primaryText="No information to show"
                        secondaryText="Use the search above to find a patient"
                        size="small"
                    />
                </div>
            }
        />
    </div>,
];

export const WritingBestPractices = () => (
    <div>
        <Page
            pageTitle="Writing Best Practices"
            pageDescription={
                <p>
                    This section provides a comprehensive guide on our writing practices. By
                    following these best practices, you&#39;ll ensure that all messaging and text
                    reflects our brand&#39;s voice and tone.
                </p>
            }
            pageContent={
                <>
                    <Section title="Follow Basic Grammar Rules" contentArray={grammar} />
                    <Section title="Capitalization" contentArray={capitalization} />
                    <Section title="Numerals" contentArray={numerals} />
                    <Section title="Text Alignment" contentArray={textAlignment} />
                </>
            }
        />
    </div>
);
